/**
 * The landing-page scene: someone explaining at a board, to people working.
 *
 * Inline SVG rather than an image file, for three reasons. It costs no extra
 * request and no layout shift; it themes off the same CSS variables as the
 * rest of the page, so it cannot drift out of palette the way an exported PNG
 * does; and it stays sharp at every size without shipping three raster
 * variants.
 *
 * Drawn flat, in ink and ochre and marker-red, on the same hairline language
 * as the rubric table and the feature rules. The board deliberately shows the
 * actual product — a question, an answer in the learner's own words, and a
 * mark against it — because that is the claim the headline is making, and a
 * decorative picture of Generic Students would make it less well.
 *
 * Decorative: aria-hidden, and every word on the board is also in the copy
 * beside it, so nothing is lost to a screen reader.
 */
export function HeroIllustration() {
  return (
    <svg
      className="hero-illustration"
      viewBox="0 0 640 420"
      role="img"
      aria-hidden="true"
      focusable="false"
    >
      {/* ── The board ─────────────────────────────────────────────────── */}
      <rect
        x="150" y="16" width="474" height="236" rx="3"
        fill="var(--content-bg)" stroke="var(--content-border)" strokeWidth="1.5"
      />
      {/* Ochre rule along the top, the same anchor the hero block uses. */}
      <rect x="150" y="16" width="474" height="3" fill="var(--nav-accent)" />

      {/* Question */}
      <text x="176" y="56" className="hero-svg-label">QUESTION</text>
      <text x="176" y="80" className="hero-svg-q">
        Why does the median resist outliers?
      </text>

      {/* The learner's answer, as ruled lines — legible as writing at a
          glance without pretending to be readable text. */}
      <text x="176" y="116" className="hero-svg-label">IN YOUR OWN WORDS</text>
      <g stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" opacity="0.35">
        <line x1="176" y1="132" x2="470" y2="132" />
        <line x1="176" y1="148" x2="516" y2="148" />
        <line x1="176" y1="164" x2="392" y2="164" />
      </g>

      {/* The marking. Ticks in ochre, the mark itself in marker red — the
          same two-colour split the rest of the app uses for "noted" vs
          "scored". */}
      <g
        stroke="var(--seal)" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" fill="none"
      >
        <path d="M492 128 l6 7 11 -14" />
        <path d="M538 144 l6 7 11 -14" />
      </g>

      {/* Mark box */}
      <rect
        x="176" y="190" width="86" height="38" rx="3"
        fill="var(--mark-bg)" stroke="var(--mark)" strokeWidth="1.5"
      />
      <text x="219" y="215" className="hero-svg-mark" textAnchor="middle">0.8</text>
      <text x="278" y="206" className="hero-svg-note">reasoning shown,</text>
      <text x="278" y="222" className="hero-svg-note">one concept missing</text>

      {/* A parabola in the corner — the figure engine, which is the one thing
          on this page a competitor's screenshot never has. */}
      <g transform="translate(470 176)">
        <line x1="0" y1="52" x2="104" y2="52" stroke="var(--content-border)" strokeWidth="1" />
        <line x1="52" y1="4" x2="52" y2="60" stroke="var(--content-border)" strokeWidth="1" />
        <path
          d="M14 10 Q52 78 90 10"
          fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round"
        />
        <circle cx="52" cy="46" r="3" fill="var(--mark)" />
      </g>

      {/* ── The person explaining ─────────────────────────────────────── */}
      <g fill="var(--accent)">
        <circle cx="74" cy="120" r="19" />
        {/* Torso: a single shoulder-to-hem shape, no limbs to get wrong. */}
        <path d="M74 144 c-25 0 -38 16 -40 40 l-6 74 h92 l-6 -74 c-2 -24 -15 -40 -40 -40 z" />
      </g>
      {/* The arm that does the pointing — the whole gesture of the picture. */}
      <path
        d="M104 158 C130 150 140 130 152 112"
        fill="none" stroke="var(--accent)" strokeWidth="11" strokeLinecap="round"
      />

      {/* ── The people working ────────────────────────────────────────── */}
      {/* Seen from behind, as in any room where somebody is presenting. Heads
          and shoulders only: a back view needs no faces, and faces at this
          size are where flat illustration goes wrong. */}
      <g fill="var(--accent-dark)">
        <circle cx="214" cy="322" r="26" />
        <path d="M214 352 c-38 0 -60 22 -66 68 h132 c-6 -46 -28 -68 -66 -68 z" />
      </g>
      <g fill="var(--text-secondary)">
        <circle cx="378" cy="310" r="28" />
        <path d="M378 342 c-40 0 -64 24 -70 78 h140 c-6 -54 -30 -78 -70 -78 z" />
      </g>
      <g fill="var(--accent-dark)">
        <circle cx="540" cy="324" r="25" />
        <path d="M540 353 c-37 0 -58 21 -64 67 h128 c-6 -46 -27 -67 -64 -67 z" />
      </g>

      {/* The desk they are working at. One hairline, the same weight as the
          rules under the feature columns — it stops the group floating
          without drawing a piece of furniture nobody came to look at. */}
      <line
        x1="150" y1="404" x2="624" y2="404"
        stroke="var(--content-border)" strokeWidth="1.5"
      />

      {/* Open laptops, seen from behind in the gaps between shoulders. Drawn
          after the figures so they sit in front, which is the only cue that
          says "desk" rather than "floating rectangle". */}
      <g fill="var(--accent-light)" stroke="var(--accent)" strokeWidth="1.5" strokeLinejoin="round">
        <g transform="translate(288 404)">
          <path d="M2 0 l7 -28 h30 l7 28 z" />
          <path d="M-4 0 h56 l-4 6 h-48 z" />
        </g>
        <g transform="translate(452 404)">
          <path d="M2 0 l7 -28 h30 l7 28 z" />
          <path d="M-4 0 h56 l-4 6 h-48 z" />
        </g>
      </g>
    </svg>
  );
}
